import { type DurationString, isDurationString, isRelativeTimeValue } from '@chronosphereio/core';
import { type QueryParamConfig } from 'use-query-params';

/**
 * Gets a config object for use with `use-query-params` for encoding/decoding a time range in the query string.
 */
export function getTimeRangeQueryParamsConfig(startParamName: string, endParamName: string) {
  return {
    [startParamName]: StartParamConfig,
    [endParamName]: EndParamConfig,
  };
}

/**
 * The unprefixed / unscoped param name used for the start of a time range.
 */
const START_PARAM_NAME = 'start';

export function getStartParamName(urlParamsScope?: string) {
  return `${urlParamsScope ?? ''}${START_PARAM_NAME}`;
}

export type StartParamValue = Date | DurationString | string | undefined;

/**
 * Config for `use-query-params` for the `start` parameter used by time ranges in the query string.
 */
const StartParamConfig: QueryParamConfig<StartParamValue> = {
  encode: (value) => {
    if (value === undefined) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.getTime().toString();
  },
  decode: (value) => {
    if (value === null || value === undefined || Array.isArray(value)) {
      return undefined;
    }

    // Handle legacy relative time format by converting to `now-X` format (to support old links with time ranges)
    if (isDurationString(value)) {
      return value;
    }

    // Handle new 'now' and 'now-x' format
    if (isRelativeTimeValue(value)) {
      return value;
    }

    // Handle dates which use unix epoch ms
    const dateMs = Number(value);
    if (!isNaN(dateMs)) {
      return new Date(dateMs);
    }

    // Otherwise we got some other string we don't know how to handle
    return undefined;
  },
};

/**
 * The unprefixed / unscoped param name used for the end of a time range.
 */
const END_PARAM_NAME = 'end';

export function getEndParamName(urlParamsScope?: string) {
  return `${urlParamsScope ?? ''}${END_PARAM_NAME}`;
}

export type EndParamValue = Date | string | undefined;

/**
 * Config for `use-query-params` for the `end` parameter used by time ranges in the query string.
 */
const EndParamConfig: QueryParamConfig<EndParamValue> = {
  encode: (value) => {
    if (value === undefined) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.getTime().toString();
  },
  decode: (value) => {
    if (value === null || value === undefined || Array.isArray(value)) {
      return undefined;
    }

    // Handle new 'now' and 'now-x' format
    if (isRelativeTimeValue(value)) {
      return value;
    }

    // Handle dates which use unix epoch ms
    const dateMs = Number(value);
    if (!isNaN(dateMs)) {
      return new Date(dateMs);
    }

    // Otherwise we got some other string we don't know how to handle
    return undefined;
  },
};
